import type { StateData } from '@/lib/become-an-agent-types';
import { Camera } from 'lucide-react';

const stateOutlines = import.meta.glob('../../svgs/states/*.svg', {
    query: '?raw',
    import: 'default',
    eager: true,
}) as Record<string, string>;

function getStateOutline(slug: string): string | null {
    const key = Object.keys(stateOutlines).find((k) => k.endsWith(`/${slug}.svg`));
    return key ? stateOutlines[key] : null;
}

export function Hero({ state, guideYear }: { state: StateData; guideYear?: number }) {
    const outline = getStateOutline(state.slug);
    const creditLabel = state.hero_credit_text || state.hero_credit_photographer;
    const creditHref = state.hero_credit_text ? null : state.hero_credit_url;

    return (
        <section className="grid w-full grid-cols-1 items-center gap-10 bg-[#0b2222] px-6 py-12 sm:px-10 sm:py-16 md:min-h-[70vh] md:grid-cols-2 md:gap-12 lg:px-16 lg:py-20 xl:px-20">
            {/* ── Left panel — content, centered ──────────────── */}
            <div className="flex flex-col items-center text-center">
                <div className="mb-5 flex flex-col items-center gap-1.5">
                    <div className="lux-caps lux-caps-sm flex items-center gap-2.5 text-[#E8E0D8]/55">
                        {/* nosemgrep: react-dangerouslysetinnerhtml-no-sanitize — compiled state-outline SVG chosen from an import.meta.glob(?raw) asset set, never supplied markup */}
                        {outline && (
                            <span
                                className="inline-flex h-4 items-center text-[#E8E0D8]/55 [&>svg]:h-full [&>svg]:w-auto"
                                aria-hidden="true"
                                dangerouslySetInnerHTML={{ __html: outline }}
                            />
                        )}
                        <span>{state.state} &middot; Licensing &amp; Training</span>
                    </div>
                    <p className="lux-caps lux-caps-sm text-[#E8E0D8]/40">8 min read</p>
                </div>

                <h1
                    className="font-didot max-w-2xl text-[#E8E0D8]"
                    style={{ fontSize: 'clamp(2rem, 4.5vw, 3.75rem)', lineHeight: 1.05 }}
                >
                    How to Become a Travel Agent in {state.state}{' '}
                    <span className="text-[#E8E0D8]/60">({guideYear ?? new Date().getFullYear()} Guide)</span>
                </h1>

                <div className="mt-8">
                    <a
                        href="/become-an-agent"
                        className="lux-caps lux-caps-sm inline-block rounded-sm border border-obsidian bg-obsidian px-4 py-1.5 text-[#E8E0D8] transition-colors duration-300 hover:bg-[#0b2222]"
                    >
                        Apply to Our Program
                    </a>
                    <p
                        className="mt-2 text-[10px] leading-snug text-[#E8E0D8]/70"
                        style={{ letterSpacing: '0.02em' }}
                    >
                        Free training. Licensed in your state.
                        <br />
                        Start selling in 3-6 weeks.
                    </p>
                    {/* Credential row — Forbes / IATA / TLN / ARC. Same opacity-50 +
                        brightness-invert treatment used in AgentApplyForm so the cluster
                        reads as one recessive trust mark, not four shouting logos. */}
                    <div className="mt-10 flex items-center justify-center gap-6">
                        <img
                            src="/EndorsedByLogo-FTG-REV.png"
                            alt="Forbes Travel Guide Endorsed"
                            loading="lazy"
                            className="h-9 w-auto object-contain opacity-50"
                        />
                        <img
                            src="/iata.svg"
                            alt="IATA"
                            loading="lazy"
                            className="h-6 w-auto object-contain opacity-50 brightness-0 invert"
                        />
                        <img
                            src="/TLN-logo.png"
                            alt="Travel Leaders Network"
                            loading="lazy"
                            className="h-6 w-auto object-contain opacity-50 brightness-0 invert"
                        />
                        <img
                            src="/arc.png"
                            alt="ARC"
                            loading="lazy"
                            className="h-6 w-auto object-contain opacity-50 brightness-0 invert"
                        />
                    </div>
                </div>
            </div>

            {/* ── Right panel — photo, inset by section padding ─ */}
            <div className="relative h-full min-h-[55vh] overflow-hidden rounded-md md:min-h-0">
                {state.main_image && (
                    <img
                        src={state.main_image}
                        alt={`Travel scene representing ${state.state}`}
                        className="editorial-filter absolute inset-0 h-full w-full object-cover"
                    />
                )}

                {creditLabel && (
                    <div className="absolute right-4 bottom-3 z-10 sm:right-6 sm:bottom-5">
                        {creditHref ? (
                            <a
                                href={creditHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={`Photo by ${creditLabel}`}
                                className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] tracking-[0.15em] backdrop-blur-sm transition-colors"
                                style={{ backgroundColor: 'rgba(101, 62, 32, 0.4)', color: 'rgba(245, 245, 245, 0.92)' }}
                            >
                                <Camera size={10} style={{ stroke: 'rgba(245, 245, 245, 0.9)' }} />
                                {creditLabel}
                            </a>
                        ) : (
                            <span
                                className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] tracking-[0.15em] backdrop-blur-sm"
                                style={{ backgroundColor: 'rgba(101, 62, 32, 0.4)', color: 'rgba(245, 245, 245, 0.92)' }}
                            >
                                <Camera size={10} style={{ stroke: 'rgba(245, 245, 245, 0.9)' }} />
                                {creditLabel}
                            </span>
                        )}
                    </div>
                )}
            </div>
        </section>
    );
}
